import React from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Diversity3,
  Grading,
  ListAlt,
  SmsFailed,
  AccountTreeOutlined,
  ManageSearch,
  Construction,
  Sort,
  Task,
} from '@mui/icons-material';
import { GSApplicationList } from './components/GSApplicationList';
import {
  GSDecisionList,
  GSEmailList,
  GSOfferList,
  GSManagement,
  FailedList,
  ViewPriority,
  gsConditionToMeetNumFilter,
} from './components';

import { PAGE_TEXT } from '../lang';
import { AssessmentSidebar, GS_ASSESSMENT_MODULE } from '../components';
import { FunctionHub } from '../DRCAssessment';
import { type SidebarData } from '../../../components';
import { DualJointManagementList } from '../../DualJointManagement/DualJointManagementList';
import { ScholarshipList } from '../../ScholarshipManagement';
import { GSConditionCheckList } from './components/GSConditionCheck/GSConditionCheckList';

export const GSAssessment: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  let activatedModuleId = searchParams.get('am') as GS_ASSESSMENT_MODULE;

  if (!Object.values(GS_ASSESSMENT_MODULE).includes(activatedModuleId)) {
    activatedModuleId = GS_ASSESSMENT_MODULE.APPLICATION_OVERVIEW;
  }

  const sidebarData: SidebarData[] = [
    {
      id: GS_ASSESSMENT_MODULE.APPLICATION_OVERVIEW,
      name: PAGE_TEXT.APPLICATION_OVERVIEW.TITLE,
      icon: <Grading />,
    },
    {
      id: GS_ASSESSMENT_MODULE.PANEL_MANAGEMENT,
      name: PAGE_TEXT.PANEL_MANAGEMENT.TITLE,
      icon: <Diversity3 />,
    },
    {
      id: GS_ASSESSMENT_MODULE.TO_DO_LIST,
      name: PAGE_TEXT.TO_DO_LIST.TITLE,
      icon: <ListAlt />,
      children: [
        {
          id: GS_ASSESSMENT_MODULE.MAKE_DECISION,
          name: PAGE_TEXT.MAKE_DECISION.TITLE,
        },
        {
          id: GS_ASSESSMENT_MODULE.EMAIL_PROCESS,
          name: PAGE_TEXT.EMAIL_PROCESS.TITLE,
        },
        {
          id: GS_ASSESSMENT_MODULE.OFFER_LIST,
          name: PAGE_TEXT.OFFER_LIST.TITLE,
        },
      ],
    },
    {
      id: GS_ASSESSMENT_MODULE.FAILED_LIST,
      name: PAGE_TEXT.FAILED_LIST.TITLE,
      icon: <SmsFailed />,
    },
    {
      id: GS_ASSESSMENT_MODULE.DUAL_JOINT_MANAGEMENT,
      name: PAGE_TEXT.DUAL_JOINT_MANAGEMENT.TITLE,
      icon: <AccountTreeOutlined />,
    },
    {
      id: GS_ASSESSMENT_MODULE.SCHOLARSHIP_MANAGEMENT,
      name: PAGE_TEXT.SCHOLARSHIP_MANAGEMENT.TITLE,
      icon: <ManageSearch />,
    },
    {
      id: GS_ASSESSMENT_MODULE.FUNCTION_HUB,
      name: PAGE_TEXT.FUNCTION_HUB.TITLE,
      icon: <Construction />,
    },
    {
      id: GS_ASSESSMENT_MODULE.VIEW_PRIORITY,
      name: PAGE_TEXT.VIEW_PRIORITY.TITLE,
      icon: <Sort />,
    },
    {
      id: GS_ASSESSMENT_MODULE.MEET_CONDITION_CHECK,
      name: PAGE_TEXT.MEET_CONDITION_CHECK.TITLE,
      icon: <Task />,
    },
  ];

  const handleChangeActivated = (activatedId: string): void => {
    const keys = Array.from(searchParams.keys());
    // delete unnecessary param
    keys.forEach(key => {
      if (key !== 'am') {
        searchParams.delete(key);
      }
    });
    searchParams.set('am', activatedId);
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const assessmentModulesMap: Partial<
    Record<GS_ASSESSMENT_MODULE, { component: any; moduleTitle: string }>
  > = {
    [GS_ASSESSMENT_MODULE.APPLICATION_OVERVIEW]: {
      // TODO would replaced by <OverView /> when nominationPaginated API updates
      component: <GSApplicationList />,
      moduleTitle: PAGE_TEXT.APPLICATION_OVERVIEW.TITLE,
    },
    [GS_ASSESSMENT_MODULE.PANEL_MANAGEMENT]: {
      component: <GSManagement />,
      moduleTitle: PAGE_TEXT.PANEL_MANAGEMENT.SUBTITLE,
    },
    [GS_ASSESSMENT_MODULE.MAKE_DECISION]: {
      component: <GSDecisionList />,
      moduleTitle: PAGE_TEXT.MAKE_DECISION.TITLE,
    },
    [GS_ASSESSMENT_MODULE.EMAIL_PROCESS]: {
      component: <GSEmailList />,
      moduleTitle: PAGE_TEXT.EMAIL_PROCESS.TITLE,
    },
    [GS_ASSESSMENT_MODULE.OFFER_LIST]: {
      component: <GSOfferList />,
      moduleTitle: PAGE_TEXT.OFFER_LIST.TITLE,
    },
    [GS_ASSESSMENT_MODULE.FAILED_LIST]: {
      component: <FailedList />,
      moduleTitle: PAGE_TEXT.FAILED_LIST.TITLE,
    },
    [GS_ASSESSMENT_MODULE.DUAL_JOINT_MANAGEMENT]: {
      component: <DualJointManagementList />,
      moduleTitle: PAGE_TEXT.DUAL_JOINT_MANAGEMENT.TITLE,
    },
    [GS_ASSESSMENT_MODULE.SCHOLARSHIP_MANAGEMENT]: {
      component: <ScholarshipList />,
      moduleTitle: PAGE_TEXT.SCHOLARSHIP_MANAGEMENT.TITLE,
    },
    [GS_ASSESSMENT_MODULE.FUNCTION_HUB]: {
      component: <FunctionHub />,
      moduleTitle: PAGE_TEXT.FUNCTION_HUB.TITLE,
    },
    [GS_ASSESSMENT_MODULE.VIEW_PRIORITY]: {
      component: <ViewPriority />,
      moduleTitle: PAGE_TEXT.VIEW_PRIORITY.TITLE,
    },
    [GS_ASSESSMENT_MODULE.MEET_CONDITION_CHECK]: {
      component: <GSConditionCheckList filter={gsConditionToMeetNumFilter()} />,
      moduleTitle: PAGE_TEXT.MEET_CONDITION_CHECK.TITLE,
    },
  };
  const activatedModule:
    | { component: React.ReactNode; moduleTitle: string }
    | undefined = assessmentModulesMap[activatedModuleId];

  const moduleComponent = activatedModule?.component ?? activatedModuleId;
  const moduleTitle = activatedModule?.moduleTitle ?? activatedModuleId;

  return (
    <PageContainer>
      <div className="sidebar-area">
        <AssessmentSidebar
          onChangeActivated={handleChangeActivated}
          collapsible={true}
          currentActivatedModule={activatedModuleId}
          sidebarData={sidebarData}
          drawerWidth={275}
          drawerInnerWidth="16.75rem"
        />
      </div>
      <Container>
        <h1>{moduleTitle}</h1>
        <div className="form-area">{moduleComponent}</div>
      </Container>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;
  h1 {
    margin-top: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    text-align: center;
    color: #951d1d;
  }
  .sidebar-area {
    width: min-content;
  }
`;

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 70%;

  .form-area {
    flex: 0.95;
    margin: 1rem;
  }

  .submit-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-top: 2rem;
    padding: 0.5rem 1rem;
    button {
      margin: 0 1rem;
    }
  }
`;
